import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Info } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Iter 110g — 90-day per-component uptime grid.
// Backend snapshots the incident.io Widget API every 15 min into
// `incident_io_snapshots` and aggregates per-day worst-status. Grid
// grows over time — first-day copy is explicit that history builds up
// as Conduit runs.

const STATUS_STYLE = {
  operational:          { bg: "bg-emerald-500",  label: "Operational" },
  under_maintenance:    { bg: "bg-blue-400",     label: "Maintenance" },
  degraded_performance: { bg: "bg-amber-400",    label: "Degraded" },
  partial_outage:       { bg: "bg-orange-500",   label: "Partial outage" },
  full_outage:          { bg: "bg-red-500",      label: "Full outage" },
  no_data:              { bg: "bg-slate-200",    label: "No data" },
};

function niceDate(iso) {
  try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}

function Row({ label, days, testid }) {
  return (
    <div className="flex items-center gap-3 py-1" data-testid={testid}>
      <div className="w-40 shrink-0 truncate text-xs font-medium text-slate-700" title={label}>{label}</div>
      <div className="flex flex-1 gap-[2px]">
        {days.map((d) => {
          const meta = STATUS_STYLE[d.status] || STATUS_STYLE.no_data;
          return (
            <TooltipProvider key={d.date} delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`h-6 flex-1 min-w-[3px] rounded-[2px] ${meta.bg} transition-transform hover:scale-y-125 origin-center`}
                    data-testid={`uptime-cell-${d.date}`}
                    data-status={d.status}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="font-medium">{niceDate(d.date)}</div>
                  <div className={`text-[11px] ${d.status === "no_data" ? "text-slate-400" : ""}`}>{meta.label}</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
}

export function StatusUptimeGrid() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = usePersistedState("uptime.days", 90);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/status/uptime-grid", { params: { days } });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading && !data) {
    return (
      <Card className="border-slate-200 p-4" data-testid="status-uptime-loading">
        <div className="flex items-center gap-2 text-slate-400 text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading uptime history…
        </div>
      </Card>
    );
  }
  if (!data || !data.configured) return null;

  const range = data.date_range || [];
  const rangeLabel = range.length ? `${niceDate(range[0])} – ${niceDate(range[range.length - 1])}` : "";

  return (
    <Card className="border-slate-200 p-4" data-testid="status-uptime-grid">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h4 className="font-heading text-sm font-semibold text-slate-800">Uptime history</h4>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {rangeLabel}
            <span className="ml-2 text-slate-400">·</span>
            <span className="ml-2">{data.days_covered} of {data.days} days recorded</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          {[30, 60, 90].map((n) => (
            <button key={n} onClick={() => setDays(n)}
                    className={`rounded px-2 py-0.5 text-[11px] ${days === n ? "bg-primary/10 font-medium text-primary" : "text-slate-500 hover:bg-slate-100"}`}
                    data-testid={`uptime-days-${n}`}>
              {n}d
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-7 gap-1" data-testid="uptime-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <Row label="Overall" days={data.overall_days || []} testid="uptime-row-overall" />
        {(data.components || []).length > 0 && (
          <>
            <div className="my-2 h-px bg-slate-100" />
            {data.components.map((c) => (
              <Row key={c.id} label={c.name} days={c.days} testid={`uptime-row-${c.id}`} />
            ))}
          </>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] text-slate-500">
        {["operational", "under_maintenance", "degraded_performance", "partial_outage", "full_outage", "no_data"].map((k) => (
          <div key={k} className="flex items-center gap-1.5">
            <div className={`h-2.5 w-2.5 rounded-[2px] ${STATUS_STYLE[k].bg}`} />
            <span>{STATUS_STYLE[k].label}</span>
          </div>
        ))}
      </div>

      {data.days_covered < data.days && (
        <div className="mt-3 flex items-start gap-1.5 rounded border border-blue-100 bg-blue-50 px-2.5 py-1.5 text-[10px] text-blue-700">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            History is being built from live Widget API snapshots (every 15 min). Cells without data existed
            before this deployment started snapshotting on <span className="font-medium">{data.first_seen_at ? new Date(data.first_seen_at).toLocaleDateString() : "—"}</span>.
            The grid fills in over time.
          </span>
        </div>
      )}
    </Card>
  );
}
